import React from 'react';
import {
	Controller,
	type Control,
	type ControllerProps,
	type FieldValues,
	type FieldPath,
} from 'react-hook-form';
import {i18n} from '@netx/core/i18n';

export type WithReactHookFormControlFieldProps<
	P extends {value?: any} = {value?: any},
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	C extends Control<TFieldValues> = Control<TFieldValues>,
> = Omit<P, 'value'> & {
	control: C;
	ControllerProps?: Omit<
		ReactHookFormControlProps<TFieldValues, TName>,
		'render' | 'name' | 'control'
	>;
};

export interface ReactHookFormControlProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends ControllerProps<TFieldValues, TName> {}

export const ReactHookFormControlPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	control,
	rules,
	...props
}: ReactHookFormControlProps<TFieldValues, TName>) => {
	const required = rules?.required != null && rules.required !== false;
	const requireMessage =
		typeof rules?.required === 'string' ? rules.required : undefined;

	return (
		<Controller<TFieldValues, TName>
			{...props}
			control={control}
			rules={{
				...rules,
				required: required
					? requireMessage ?? i18n.t('i18n.required')
					: undefined,
			}}
		/>
	);
};

/** `react-hook-form` control wrapper */
export const ReactHookFormControl = ReactHookFormControlPrimitive;

export default ReactHookFormControl;
